import { useMemo, useState } from 'react';
import type { Evento } from '@eventos-cien/shared';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { apiAuth, ApiError } from '../lib/apiAuth';
import { usePanelData } from './PanelDataProvider';

function ligaDe(token: string): string {
  return `${window.location.origin}/i/${token}`;
}

export const PLACEHOLDERS_PLANTILLA = ['{{nombre}}', '{{fecha}}', '{{hora}}', '{{lugar}}', '{{liga}}'] as const;

function mensajeDefault1(evento: Evento | null): string {
  const nombre = evento?.nombre ?? 'nuestro evento';
  const cuando = evento?.fechaTexto ? `\n📅 ${evento.fechaTexto}` : '';
  const aQueHora = evento?.horaTexto ? `\n🕐 ${evento.horaTexto}` : '';
  const donde = evento?.lugarTexto ? `\n📍 ${evento.lugarTexto}` : '';
  return `¡Hola! Te comparto tu invitación para ${nombre}.${cuando}${aQueHora}${donde}`;
}

function mensajeDefault2(token: string): string {
  return `Confirma tu asistencia aquí:\n${ligaDe(token)}\n\nLa liga es personal y de un solo uso.`;
}

function aplicarPlaceholders(plantilla: string, evento: Evento | null, token: string): string {
  return plantilla
    .replaceAll('{{nombre}}', evento?.nombre ?? 'nuestro evento')
    .replaceAll('{{fecha}}', evento?.fechaTexto ?? '')
    .replaceAll('{{hora}}', evento?.horaTexto ?? '')
    .replaceAll('{{lugar}}', evento?.lugarTexto ?? '')
    .replaceAll('{{liga}}', ligaDe(token));
}

function mensaje1(evento: Evento | null, token: string): string {
  const plantilla = evento?.mensajeTexto1;
  if (!plantilla) return mensajeDefault1(evento);
  return aplicarPlaceholders(plantilla, evento, token);
}

function mensaje2(evento: Evento | null, token: string): string {
  const plantilla = evento?.mensajeTexto2;
  if (!plantilla) return mensajeDefault2(token);
  return aplicarPlaceholders(plantilla, evento, token);
}

export function MisInvitacionesScreen() {
  const { user } = useAuth();
  const { eventoId, evento, invitaciones: todas } = usePanelData();
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  // Estados para los filtros de la tabla
  const [filtroEstado, setFiltroEstado] = useState<'por_enviar' | 'entregada' | 'registrada'>('por_enviar');
  const [filtroCupo, setFiltroCupo] = useState<'todos' | 1 | 2>('todos');

  const invitaciones = useMemo(
    () => todas.filter((i) => i.asesorUid === user?.uid),
    [todas, user],
  );

  const grupos = useMemo(() => {
    return {
      porEnviar: invitaciones.filter((i) => i.estado === 'disponible' || i.estado === 'en_mano'),
      entregadas: invitaciones.filter((i) => i.estado === 'entregada'),
      registradas: invitaciones.filter((i) => i.estado === 'registrada'),
    };
  }, [invitaciones]);

  // Filtro activo para la tabla
  const mostradas = useMemo(() => {
    return invitaciones.filter((i) => {
      const estadoAgrupado = (i.estado === 'disponible' || i.estado === 'en_mano') ? 'por_enviar' : i.estado;
      if (estadoAgrupado !== filtroEstado) return false;
      if (filtroCupo !== 'todos' && i.cupo !== filtroCupo) return false;
      return true;
    });
  }, [invitaciones, filtroEstado, filtroCupo]);

  async function accion(token: string, ruta: 'entregada' | 'devolver') {
    setError(null);
    setOcupado(token);
    try {
      await apiAuth(`/asesor/${eventoId}/${token}/${ruta}`, { method: 'POST' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No se pudo completar');
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="w-full max-w-full space-y-4 sm:space-y-6">
      {/* Contadores globales: grid adaptativo sin scroll */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat n={grupos.porEnviar.length} etiqueta="Por enviar" bg="bg-blue-50/50" />
        <Stat n={grupos.entregadas.length} etiqueta="Enviadas" bg="bg-white border border-borde" />
        <Stat n={grupos.registradas.length} etiqueta="Registrados" bg="bg-emerald-50/50" />
      </div>

      {error && <Callout tono="error">{error}</Callout>}

      {evento?.flyerUrl && (
        <Card className="flex flex-col gap-3 p-3.5 bg-navy text-white shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div className="min-w-0">
            <strong className="block text-sm">Flyer del evento</strong>
            <span className="block truncate text-xs text-white/70">Descarga la imagen para enviarla.</span>
          </div>
          <a
            href={evento.flyerUrl}
            target="_blank"
            rel="noreferrer"
            className="flex w-full items-center justify-center rounded-lg bg-white/20 py-2 text-sm font-medium transition hover:bg-white/30 sm:w-auto sm:px-4"
          >
            Descargar
          </a>
        </Card>
      )}

      {/* SECCIÓN DE LA TABLA (FILTROS + LISTA) */}
      <section className="w-full space-y-3 sm:space-y-4">
        {/* Filtros Superiores: flex-wrap para 0 scroll horizontal */}
        <div className="flex w-full flex-col gap-2.5">
          <div className="flex w-full flex-wrap gap-1.5 sm:gap-2">
            <Chip activo={filtroEstado === 'por_enviar'} onClick={() => setFiltroEstado('por_enviar')}>
              Por enviar ({grupos.porEnviar.length})
            </Chip>
            <Chip activo={filtroEstado === 'entregada'} onClick={() => setFiltroEstado('entregada')}>
              Enviadas ({grupos.entregadas.length})
            </Chip>
            <Chip activo={filtroEstado === 'registrada'} onClick={() => setFiltroEstado('registrada')}>
              Registrados ({grupos.registradas.length})
            </Chip>
          </div>

          <div className="flex w-full flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setFiltroCupo('todos')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${filtroCupo === 'todos' ? 'bg-navy text-white' : 'bg-navy/5 text-texto'}`}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setFiltroCupo(1)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${filtroCupo === 1 ? 'bg-navy text-white' : 'bg-navy/5 text-texto'}`}
            >
              Individuales 👤
            </button>
            <button
              type="button"
              onClick={() => setFiltroCupo(2)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${filtroCupo === 2 ? 'bg-navy text-white' : 'bg-navy/5 text-texto'}`}
            >
              Parejas 👥
            </button>
          </div>
        </div>

        {/* Tarjeta Tipo Tabla */}
        <Card className="w-full overflow-hidden bg-white shadow-sm">
          <div className="divide-y divide-borde/50">
            {mostradas.length === 0 ? (
              <div className="p-6 text-center text-sm text-texto/50 sm:p-8">
                No hay boletos en esta categoría.
              </div>
            ) : (
              mostradas.map((i) => {
                if (filtroEstado === 'registrada') {
                  return (
                    <div key={i.token} className="flex w-full items-center justify-between p-3.5 transition hover:bg-slate-50 sm:p-4">
                      <div className="flex min-w-0 flex-1 flex-col pr-2">
                        <strong className="truncate text-sm text-texto">{i.invitado.nombre || 'Sin nombre'}</strong>
                        {i.invitado.acompanante ? (
                          <span className="truncate text-xs text-texto/50">+ {i.invitado.acompanante}</span>
                        ) : (
                          i.cupo === 2 && (
                            <span className="truncate text-xs italic text-texto/40">Sin acompañante</span>
                          )
                        )}
                      </div>
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                        {i.cupo === 2 ? '👥 Pareja' : '👤 Ind.'}
                      </span>
                    </div>
                  );
                }

                return (
                  <FichaFila
                    key={i.token}
                    token={i.token}
                    cupo={i.cupo}
                    mensaje1={mensaje1(evento, i.token)}
                    mensaje2={mensaje2(evento, i.token)}
                    flyerUrl={evento?.flyerUrl ?? null}
                    ocupado={ocupado === i.token}
                    esEntregada={filtroEstado === 'entregada'}
                    onCopiar={() => filtroEstado === 'por_enviar' ? accion(i.token, 'entregada') : undefined}
                    onDevolver={() => accion(i.token, 'devolver')}
                  />
                );
              })
            )}
          </div>
        </Card>
      </section>
    </div>
  );
}

function Stat({ n, etiqueta, bg }: { n: number; etiqueta: string; bg: string }) {
  return (
    <Card className={`p-2.5 text-center sm:p-4 ${bg}`}>
      <span className="block text-xl font-bold text-texto sm:text-2xl">{n}</span>
      <span className="mt-0.5 block truncate text-[11px] font-medium text-texto/60 sm:text-xs">{etiqueta}</span>
    </Card>
  );
}

function FichaFila({
  token,
  cupo,
  mensaje1,
  mensaje2,
  flyerUrl,
  ocupado,
  esEntregada,
  onCopiar,
  onDevolver,
}: {
  token: string;
  cupo: 1 | 2;
  mensaje1: string;
  mensaje2: string;
  flyerUrl: string | null;
  ocupado: boolean;
  esEntregada: boolean;
  onCopiar?: () => void;
  onDevolver: () => void;
}) {
  // WhatsApp siempre prioriza la imagen si el portapapeles tiene imagen+texto a la vez
  // (pegar de nuevo solo repite la imagen), asi que cada paso (texto 1, imagen, texto 2)
  // usa su propia copia al portapapeles, con su propio Ctrl+V.
  const [listo, setListo] = useState<'texto1' | 'imagen' | 'texto2' | null>(null);

  async function copiarTexto1() {
    try {
      await navigator.clipboard.writeText(mensaje1);
      setListo('texto1');
      setTimeout(() => setListo(null), 2500);
    } catch {
      // Fallback silencioso: el usuario puede seleccionar el texto manualmente
    }
  }

  async function copiarImagen() {
    if (!flyerUrl) return;
    try {
      const respuesta = await fetch(flyerUrl, { mode: 'cors' });
      const blobOriginal = await respuesta.blob();
      const bitmap = await createImageBitmap(blobOriginal);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('sin contexto de canvas');
      ctx.drawImage(bitmap, 0, 0);
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
      if (!png) throw new Error('no se pudo generar el PNG');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      setListo('imagen');
      setTimeout(() => setListo(null), 2500);
    } catch (e) {
      console.warn('No se pudo copiar la imagen del flyer (revisa CORS del link):', e);
      window.open(flyerUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async function copiarTexto2() {
    try {
      await navigator.clipboard.writeText(mensaje2);
      setListo('texto2');
      setTimeout(() => setListo(null), 2500);
    } catch {
      // Fallback silencioso: el usuario puede seleccionar el texto manualmente
    }
    onCopiar?.();
  }

  return (
    <div className="w-full p-3.5 transition hover:bg-slate-50 sm:p-4">
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5 pr-2">
          <span className="truncate text-xs font-medium text-texto sm:text-sm">
            {cupo === 2 ? '👥 Pareja' : '👤 Ind.'}
          </span>
          <code className="truncate font-mono text-[11px] text-texto/50 sm:text-xs">…{token.slice(-6)}</code>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:gap-2">
          {!esEntregada && (
            <button
              type="button"
              className="px-1.5 py-1 text-xs font-medium text-red-500/70 transition hover:text-red-700 disabled:opacity-50"
              onClick={onDevolver}
              disabled={ocupado}
            >
              Devolver
            </button>
          )}

          <Button
            variante="secundario"
            className="bg-slate-100 px-3 py-1.5 text-xs sm:px-4 sm:text-sm"
            onClick={() => void copiarTexto1()}
            disabled={ocupado}
          >
            {listo === 'texto1' ? 'Texto 1 ✓' : '1. Texto'}
          </Button>

          {flyerUrl && (
            <Button
              variante="secundario"
              className="bg-slate-100 px-3 py-1.5 text-xs sm:px-4 sm:text-sm"
              onClick={() => void copiarImagen()}
              disabled={ocupado}
            >
              {listo === 'imagen' ? 'Imagen ✓' : '2. Imagen'}
            </Button>
          )}

          <Button
            variante={esEntregada ? 'secundario' : 'primario'}
            className={esEntregada ? 'bg-slate-100 px-3 py-1.5 text-xs sm:px-4 sm:text-sm' : 'bg-navy px-3.5 py-1.5 text-xs text-white shadow-sm sm:px-5 sm:text-sm'}
            onClick={() => void copiarTexto2()}
            disabled={ocupado}
            cargando={ocupado}
          >
            {listo === 'texto2'
              ? 'Texto 2 ✓'
              : flyerUrl
                ? '3. Texto'
                : (esEntregada ? 'Re-copiar' : 'Copiar y marcar')}
          </Button>
        </div>
      </div>

      {flyerUrl && (
        <p className="mt-2 text-right text-[11px] text-texto/60">
          Pega cada paso en WhatsApp en orden: texto 1, imagen, texto 2 (Ctrl+V después de cada botón).
        </p>
      )}
    </div>
  );
}
